/**
 * @fileoverview Checkout Form Validation System
 *
 * This file implements comprehensive form validation using Yup schema validation
 * with sophisticated country-specific rules and conditional field validation.
 *
 * Core Features:
 * - Dynamic validation schema creation based on checkout data context
 * - Country-specific conditional validation (USA vs international)
 * - Real-time field validation with immediate feedback
 * - Comprehensive error handling and user-friendly error messages
 * - Support for both full form and individual field validation
 *
 * Validation Rules:
 * - Required field validation with empty string handling
 * - Country selection with proper type conversion and validation
 * - Conditional state/province validation based on country selection
 * - USA-specific state selection validation using USStatesID
 * - International state/province validation for non-USA countries
 *
 * Key Functions:
 * - createCheckoutValidationSchema: Dynamic schema with context awareness
 * - validateCheckoutForm: Complete form validation with error aggregation
 * - validateField: Single field validation for real-time feedback
 * - Country detection logic with flexible label matching
 *
 * Error Handling:
 * - Type-safe error interfaces with field-specific messages
 * - Graceful handling of validation library exceptions
 * - User-friendly error message formatting
 */

import * as yup from "yup";

/**
 * Helper function to check if a country ID represents USA
 *
 * This function provides robust USA detection by checking country labels
 * rather than hardcoding country IDs, making it more maintainable.
 *
 * @param countriesId - Country identifier to check
 * @param context - Context object containing country list data
 * @returns True if the country represents USA, false otherwise
 */
const isUSACountry = (countriesId: any, context?: any): boolean => {
  // If no context or country list is available, we can't determine
  if (!context || !context.listOfCountries) {
    return false;
  }

  // Find the country in the list by its value
  const country = context.listOfCountries.find(
    (c: { label: string; value: number }) => c.value == countriesId
  );

  // Check if the country label indicates USA
  if (country) {
    const label = country.label?.toLowerCase() || "";
    const isUSA =
      label.includes("united states") ||
      label.includes("usa") ||
      label === "us" ||
      label.includes("america");
    return isUSA;
  }

  return false;
};

/**
 * Creates a dynamic validation schema for checkout form with context awareness
 *
 * This function generates a Yup validation schema that adapts based on the
 * checkout data context, particularly for country-specific field requirements
 * and site selection mode.
 *
 * @param checkoutData - Optional checkout data for context-aware validation
 * @returns Yup validation schema with conditional rules
 */
export const createCheckoutValidationSchema = (checkoutData?: any) => {
  const isAutoSelectSiteEnabled = Boolean(checkoutData?.details?.autoSelectSite);

  return yup.object().shape({
    // Site selection validation - required when autoSelectSite is enabled
    SitesId: yup.mixed().when([], {
      is: () => isAutoSelectSiteEnabled,
      then: (schema: yup.MixedSchema) =>
        schema
          .test("is-valid-site", "Please select a delivery site", (value) => {
            // Handle empty string, null, undefined cases
            if (value === "" || value == null || value === undefined) {
              return false;
            }
            // Convert to number and check if it's valid
            const num = Number(value);
            return !isNaN(num) && num > 0;
          })
          .required("Please select a delivery site"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),

    // Address fields - only validate when NOT in autoSelectSite mode
    SitesName: yup.mixed().when([], {
      is: () => !isAutoSelectSiteEnabled,
      then: (schema: yup.MixedSchema) =>
        yup.string().required("Site Name is required").trim().min(1, "Site Name cannot be empty"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),

    AddressLine1: yup.mixed().when([], {
      is: () => !isAutoSelectSiteEnabled,
      then: (schema: yup.MixedSchema) =>
        yup
          .string()
          .required("Address Line 1 is required")
          .trim()
          .min(1, "Address Line 1 cannot be empty"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),

    City: yup.mixed().when([], {
      is: () => !isAutoSelectSiteEnabled,
      then: (schema: yup.MixedSchema) =>
        yup.string().required("City is required").trim().min(1, "City cannot be empty"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),

    PostalZipCode: yup.mixed().when([], {
      is: () => !isAutoSelectSiteEnabled,
      then: (schema: yup.MixedSchema) =>
        yup
          .string()
          .required("Postal/Zip Code is required")
          .trim()
          .min(1, "Postal/Zip Code cannot be empty"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),

    CountriesId: yup.mixed().when([], {
      is: () => !isAutoSelectSiteEnabled,
      then: (schema: yup.MixedSchema) =>
        yup
          .mixed()
          .test("is-valid-country", "Please select a country", (value) => {
            // Handle empty string, null, undefined cases
            if (value === "" || value == null || value === undefined) {
              return false;
            }
            // Convert to number and check if it's valid
            const num = Number(value);
            return !isNaN(num) && num > 0;
          })
          .required("Please select a country"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),

    // Conditional validation for State/Province based on country selection and site mode
    StateProvince: yup.string().when(["CountriesId"], {
      is: (countriesId: number) => {
        // Skip validation if in autoSelectSite mode
        if (isAutoSelectSiteEnabled) return false;

        // For USA countries, StateProvince field is not used (USStatesID is used instead)
        const context = checkoutData?.details;
        const isUSA = isUSACountry(countriesId, context);
        return !isUSA; // Only validate StateProvince for NON-USA countries
      },
      then: (schema: yup.StringSchema) =>
        schema
          .required("State/Province is required")
          .trim()
          .min(1, "State/Province cannot be empty"),
      otherwise: (schema: yup.StringSchema) => schema.nullable(),
    }),

    USStatesID: yup.mixed().when(["CountriesId"], {
      is: (countriesId: number) => {
        // Skip validation if in autoSelectSite mode
        if (isAutoSelectSiteEnabled) return false;

        // For USA, USStatesID is required (this is where the state selection is stored)
        const context = checkoutData?.details;
        const isUSA = isUSACountry(countriesId, context);
        return isUSA;
      },
      then: (schema: yup.MixedSchema) =>
        schema
          .test("is-number", "Please select a US State", (value) => {
            // Handle empty string, null, undefined cases
            if (value === "" || value == null || value === undefined) {
              return false;
            }
            // Convert to number and check if it's valid
            const num = Number(value);
            const isValid = !isNaN(num) && num > 0;
            return isValid;
          })
          .required("Please select a US State"),
      otherwise: (schema: yup.MixedSchema) => schema.nullable(),
    }),
  });
};

/**
 * Validation error interface for type-safe error handling
 */
export interface ValidationErrors {
  /** Field name to error message mapping */
  [key: string]: string;
}

/**
 * Validates complete form data and returns validation errors
 *
 * @param formData - Form data to validate
 * @param checkoutData - Optional checkout context for conditional validation
 * @returns Promise resolving to validation errors object
 */
export const validateCheckoutForm = async (
  formData: Record<string, any>,
  checkoutData?: any
): Promise<ValidationErrors> => {
  try {
    // Create validation schema with checkout data context
    const schema = createCheckoutValidationSchema(checkoutData);
    const context = checkoutData?.details || {};

    await schema.validate(formData, {
      abortEarly: false,
    });
    return {};
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: ValidationErrors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return errors;
    }
    return {};
  }
};

/**
 * Validates a single field for real-time feedback
 *
 * @param fieldName - Name of the field to validate
 * @param value - Current value of the field
 * @param allFormData - Complete form data for context-dependent validation
 * @param checkoutData - Optional checkout context data
 * @returns Promise resolving to error message or null if valid
 */
export const validateField = async (
  fieldName: string,
  value: any,
  allFormData: Record<string, any>,
  checkoutData?: any
): Promise<string | null> => {
  try {
    // Create validation schema with checkout data context
    const schema = createCheckoutValidationSchema(checkoutData);
    const fieldSchema = yup.reach(schema, fieldName) as yup.Schema;
    await fieldSchema.validate(value, {
      context: allFormData, // Pass form data as context
    });
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    return null;
  }
};

/**
 * Utility function to check if form has any validation errors
 *
 * @param errors - Validation errors object to check
 * @returns True if form is valid (no errors), false if there are errors
 */
export const isFormValid = (errors: ValidationErrors): boolean => {
  return Object.keys(errors).length === 0;
};

/**
 * Retrieves validation error message for a specific field
 *
 * @param errors - Validation errors object
 * @param fieldName - Name of the field to get error for
 * @returns Error message string or undefined if no error exists
 */
export const getFieldError = (errors: ValidationErrors, fieldName: string): string | undefined => {
  return errors[fieldName];
};
